import { createElement, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Star } from 'lucide-react'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { dispatchAppEvent } from '@/src/renderer/lib/events'
import { useDataSource } from '../../../lib/dataSource'
import type { Project, Task } from '../../../lib/db/models'

type TaskUpdate = (taskId: string, updates: Partial<Omit<Task, 'id'>>) => void | Promise<void>

/**
 * **⇧⌘F an Aufgaben** (#210, bis #257 ⌥F): schaltet den Stern der Auswahl um — über den Menü-Kanal der Seite
 * (`useContextMenuBadgeUpdate`), also mit derselben Regel: Ist eine kein Favorit, werden alle einer,
 * wer schon einer ist, behält seinen Platz. Anders als Menü und Stern **meldet sich das Kürzel**
 * (mit Rückgängig), weil man sonst nichts sähe. Rückgängig stellt die alten Stellen wieder her.
 * Der Stern in den Aufgaben-Details bewegt sich dazu wie beim Klick (`favorite-shortcut`).
 */
export function useFavoriteShortcut({ getSelection, applyUpdate }: {
  /** Dieselbe Auswahl wie im Menü-Kanal. */
  getSelection: () => Set<string>
  applyUpdate: TaskUpdate
}) {
  const { t } = useTranslation()
  const source = useDataSource()
  const getSelectionRef = useRef(getSelection)
  getSelectionRef.current = getSelection
  const applyRef = useRef(applyUpdate)
  applyRef.current = applyUpdate

  return useCallback(async (clickedTaskId: string) => {
    const selection = getSelectionRef.current()
    const ids = selection.size > 1 && selection.has(clickedTaskId) ? [...selection] : [clickedTaskId]
    const rows = (await Promise.all(ids.map(id => source.getTask(id))))
      .filter((row): row is Task => !!row && !row.deletedAt && row.parentTaskId == null)
    if (rows.length === 0) return
    const on = rows.some(row => row.favoriteOrder == null)
    const changed = on ? rows.filter(row => row.favoriteOrder == null) : rows
    const before = changed.map(row => ({ id: row.id, changes: { favoriteOrder: row.favoriteOrder ?? null } }))
    await applyRef.current(clickedTaskId, { favoriteOrder: on ? Date.now() : null })
    dispatchAppEvent('favorite-shortcut', { ids: rows.map(row => row.id), favorite: on })
    showUndoToast({
      message: changed.length === 1
        ? t(on ? 'favorites.added' : 'favorites.removed')
        : t(on ? 'favorites.addedMultiple' : 'favorites.removedMultiple', { count: changed.length }),
      undoLabel: t('common.undo'),
      // Wo der Stern wirkt (#223).
      description: t(on ? 'favorites.addedText' : 'favorites.removedText'),
      undoneMessage: t(on ? 'favorites.undoneAdd' : 'favorites.undoneRemove'),
      icon: createElement(Star, { size: 18 }),
      tone: 'favorite',
      onUndo: async () => { await source.updateTasks(before) },
    })
  }, [source, t])
}

/** **⇧⌘F ohne Auswahl auf der Projektseite** (#210): der Stern des Projekts, ebenfalls mit Meldung. */
export function useProjectFavoriteShortcut(project: Project | null) {
  const { t } = useTranslation()
  const source = useDataSource()
  const projectRef = useRef(project)
  projectRef.current = project

  return useCallback(async () => {
    const current = projectRef.current
    if (!current) return
    const before = current.favoriteOrder ?? null
    const on = before == null
    await source.updateProject(current.id, { favoriteOrder: on ? Date.now() : null })
    dispatchAppEvent('favorite-shortcut', { ids: [current.id], favorite: on })
    showUndoToast({
      message: t(on ? 'favorites.added' : 'favorites.removed'),
      undoLabel: t('common.undo'),
      // Wo der Stern wirkt (#223).
      description: t(on ? 'favorites.addedText' : 'favorites.removedText'),
      undoneMessage: t(on ? 'favorites.undoneAdd' : 'favorites.undoneRemove'),
      icon: createElement(Star, { size: 18 }),
      tone: 'favorite',
      onUndo: async () => { await source.updateProject(current.id, { favoriteOrder: before }) },
    })
  }, [source, t])
}
